import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Send, Phone, Mail, MapPin, Clock, Shield, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const { t, direction } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    amount: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success(t.contact.form.success);
    setFormData({ name: '', email: '', phone: '', amount: '', company: '', message: '' });
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A55A]/30 to-transparent" />
      <div className="absolute top-20 right-0 w-64 h-64 bg-[#C9A55A]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-[#0A1628]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-6 relative">
        
        {/* Section Header */}
        <div className={`text-center mb-16 ${direction === 'rtl' ? 'rtl' : ''}`}>
          <div className="inline-flex items-center gap-3 px-5 py-2 bg-[#0A1628]/5 border border-[#0A1628]/10 rounded-full mb-6">
            <Mail className="w-4 h-4 text-[#C9A55A]" />
            <span className="text-sm font-semibold text-[#0A1628] uppercase tracking-wider">
              {direction === 'rtl' ? 'تواصل معنا' : 'Contact Us'}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            {t.contact.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C9A55A] to-transparent mx-auto mb-4" />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          
          {/* Contact Form */}
          <div className={`lg:col-span-3 ${direction === 'rtl' ? 'order-2' : ''}`}>
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${direction === 'rtl' ? 'text-right' : ''}`}>
                    {t.contact.form.name}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C9A55A] focus:border-transparent transition-all ${direction === 'rtl' ? 'text-right' : ''}`}
                    data-testid="contact-name"
                  />
                </div>
                
                {/* Email */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${direction === 'rtl' ? 'text-right' : ''}`}>
                    {t.contact.form.email}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C9A55A] focus:border-transparent transition-all ${direction === 'rtl' ? 'text-right' : ''}`}
                    data-testid="contact-email"
                  />
                </div>
                
                {/* Phone */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${direction === 'rtl' ? 'text-right' : ''}`}>
                    {t.contact.form.phone}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C9A55A] focus:border-transparent transition-all ${direction === 'rtl' ? 'text-right' : ''}`}
                    data-testid="contact-phone"
                  />
                </div>
                
                {/* Amount */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${direction === 'rtl' ? 'text-right' : ''}`}>
                    {t.contact.form.amount}
                  </label>
                  <input
                    type="text"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C9A55A] focus:border-transparent transition-all ${direction === 'rtl' ? 'text-right' : ''}`}
                    data-testid="contact-amount"
                  />
                </div>
                
                {/* Company */}
                <div className="md:col-span-2">
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${direction === 'rtl' ? 'text-right' : ''}`}>
                    {t.contact.form.company}
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C9A55A] focus:border-transparent transition-all ${direction === 'rtl' ? 'text-right' : ''}`}
                    data-testid="contact-company"
                  />
                </div>
                
                {/* Message */}
                <div className="md:col-span-2">
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${direction === 'rtl' ? 'text-right' : ''}`}>
                    {t.contact.form.message}
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    required
                    className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#C9A55A] focus:border-transparent transition-all resize-none ${direction === 'rtl' ? 'text-right' : ''}`}
                    data-testid="contact-message"
                  />
                </div>
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full mt-6 px-8 py-4 bg-gradient-to-r from-[#C9A55A] to-[#D4AF37] text-[#0A1628] rounded-lg font-bold text-lg shadow-xl hover:shadow-[0_0_30px_rgba(201,165,90,0.4)] transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}
                data-testid="contact-submit"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-[#0A1628] border-t-transparent rounded-full animate-spin" />
                    {t.contact.form.sending}
                  </>
                ) : (
                  <>
                    <Send className={`w-5 h-5 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
                    {t.contact.form.submit}
                  </>
                )}
              </button>
            </form>
          </div>
          
          {/* Contact Info */}
          <div className={`lg:col-span-2 space-y-6 ${direction === 'rtl' ? 'order-1' : ''}`}>
            
            {/* Info Card */}
            <div className="bg-[#0A1628] rounded-2xl p-8 text-white">
              <h3 className={`text-xl font-bold mb-6 ${direction === 'rtl' ? 'text-right' : ''}`}>
                {t.contact.info.title}
              </h3>
              
              <div className="space-y-6">
                <div className={`flex items-start gap-4 ${direction === 'rtl' ? 'flex-row-reverse text-right' : ''}`}>
                  <div className="w-12 h-12 bg-[#C9A55A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#C9A55A]" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{t.ui.address}</p>
                    <p className="text-gray-400">{t.contact.info.address}</p>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${direction === 'rtl' ? 'flex-row-reverse text-right' : ''}`}>
                  <div className="w-12 h-12 bg-[#C9A55A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#C9A55A]" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{t.ui.email}</p>
                    <p className="text-gray-400">{t.contact.info.email}</p>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${direction === 'rtl' ? 'flex-row-reverse text-right' : ''}`}>
                  <div className="w-12 h-12 bg-[#C9A55A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#C9A55A]" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{t.ui.phone}</p>
                    <p className="text-gray-400">{t.contact.info.phone}</p>
                  </div>
                </div>
                
                <div className={`flex items-start gap-4 ${direction === 'rtl' ? 'flex-row-reverse text-right' : ''}`}>
                  <div className="w-12 h-12 bg-[#C9A55A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#C9A55A]" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{t.ui.workingHours}</p>
                    <p className="text-gray-400">{t.contact.info.hours}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Trust Badge */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl p-6">
              <div className={`flex items-center gap-4 ${direction === 'rtl' ? 'flex-row-reverse' : ''}`}>
                <div className="w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <div className={direction === 'rtl' ? 'text-right' : ''}>
                  <p className="font-bold text-emerald-800 text-lg">
                    {t.ui.freeConsultation}
                  </p>
                  <p className="text-emerald-700 text-sm">
                    {t.ui.noObligation}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100 text-center">
                <p className="text-2xl font-bold text-[#C9A55A] mb-1">24h</p>
                <p className="text-sm text-gray-600">
                  {t.ui.responseTime}
                </p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-md border border-gray-100 text-center">
                <p className="text-2xl font-bold text-[#C9A55A] mb-1">7</p>
                <p className="text-sm text-gray-600">
                  {t.ui.languages}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
